"use client"

import {
    currentDateFormat,
    currentMonthFormat,
    ICurrentRange,
    useCleverCalendarData
} from "../utilities/CleverCalendar/utils";
import {Suspense, use, useEffect, useState} from "react";
import {IReactiveEventContentItem, ReactiveContentItem} from "../../stores/SchoolFeedStore";
import useSWR from "swr";
import moment from "moment/moment";
import {currentRangeUrlQueryFormat, getEventsForRange} from "./utils";
import {useSchoolFeedContentPathQueryParams} from "./hooks/useSchoolFeedInfiniteScroll";
import {ISchoolFeedUrlQueries} from "../../utils/SchoolBlocksUtilities";
import styles from "./styles/SchoolFeedDisplay.module.scss"
import {ListCalendar} from "../utilities/CleverCalendar/ListCalendar";
import {MonthCalendar} from "../utilities/CleverCalendar/MonthCalendar";
import SafeRouter from "../utilities/SafeRouter";
import URI from "urijs";
import {useSchoolFeedUrlQuery} from "../../hooks/useSchoolFeedData";
import {observer} from "mobx-react";
import classNames from "classnames";
import {useRouter} from "next/navigation";
import {useStores} from "../../stores/useStores";
import {withLocaleInURI} from "../../internationalization/i18nURI";
import {deleteContentItem} from "../blocks/TerracedGridTheme/Terrace";

export const SchoolFeedListCalendar = observer((props: {
    initialEventsPromise?: Promise<EventContentItem[]>,
}) => {
    const urlQuery = useSchoolFeedUrlQuery();
    const [currentRange, setCurrentRange] = useState<ICurrentRange>({
        start: moment().startOf('month').format(currentRangeUrlQueryFormat),
        end: moment().endOf('month').add(1, 'day').format(currentRangeUrlQueryFormat), // add one day to include the last day of the month due to timezone weirdness
    });
    const pathQueryParams = useSchoolFeedContentPathQueryParams();

    let initialEvents: EventContentItem[] | undefined = undefined;
    if (props.initialEventsPromise) {
        initialEvents = use(props.initialEventsPromise);
    }

    const {data: events, isLoading, mutate} = useSWR(JSON.stringify(currentRange), async (key) => {
        const parsedKey = JSON.parse(key);
        const e = await getEventsForRange(pathQueryParams, urlQuery, parsedKey.start, parsedKey.end);
        return e.map(e => new ReactiveContentItem(e));
    }, {
        revalidateOnFocus: false,
        revalidateOnMount: !props.initialEventsPromise,
        keepPreviousData: !!props.initialEventsPromise,
        dedupingInterval: 5000,
        fallbackData: initialEvents ? initialEvents.map(e => new ReactiveContentItem(e)) : undefined,
    });

    const initialDate = urlQuery.currentSelectedDate ? moment(urlQuery.currentSelectedDate, currentRangeUrlQueryFormat).format(currentDateFormat) : undefined;
    const {
        currentMonth,
        currentDate,
        nextMonth,
        prevMonth,
        setCurrentDate,
        setCurrentMonth
    } = useCleverCalendarData(setCurrentRange, initialDate);

    useEffect(() => {
        const firstEventOnCurrentDate: HTMLLIElement | null = document.querySelector(`#events-container li[data-start-date="${currentDate.format("MMM-D-YYYY")}"]`);
        if (firstEventOnCurrentDate) {
            const scrollableList = document.querySelector("#events-container");
            if (scrollableList) {
                scrollableList.scroll({
                    top: firstEventOnCurrentDate.offsetTop,
                    behavior: "smooth",
                })
            }
        }
    }, [currentDate])

    useEffect(() => {
        if (urlQuery.currentSelectedDate) {
            const nextCurrentDateMoment = moment(urlQuery.currentSelectedDate, currentRangeUrlQueryFormat);
            if (!nextCurrentDateMoment.isSame(currentDate)) {
                setCurrentDate(nextCurrentDateMoment);
                const nextCurrentMonth = nextCurrentDateMoment.format(currentMonthFormat);
                if (currentMonth !== nextCurrentMonth) {
                    setCurrentMonth(nextCurrentMonth);
                    setCurrentRange({
                        start: nextCurrentDateMoment.clone().startOf('month').format(currentRangeUrlQueryFormat),
                        end: nextCurrentDateMoment.clone().endOf('month').add(1, 'day').format(currentRangeUrlQueryFormat), // add one day to include the last day of the month due to timezone weirdness
                    })
                }
            }

        }
    }, [urlQuery])

    return <div className={styles.eventList} id={"events-container"}>
        <ListCalendar
            currentMonth={currentMonth}
            currentDate={currentDate.format(currentDateFormat)}
            setCurrentDate={date => setCurrentDate(moment(date, currentDateFormat))}
            events={events as IReactiveEventContentItem[]}
            prevMonth={prevMonth}
            nextMonth={nextMonth}
            handleDeleteEvent={async (contentItem) => {
                await deleteContentItem(contentItem);
                await mutate();
            }}
            isLoading={isLoading}
        />
    </div>
})

export function SchoolFeedMonthCalendarSkeleton(props: {
    urlQuery: ISchoolFeedUrlQueries,
    initialEventsPromise?: Promise<EventContentItem[]>,
}) {
    const [currentRange, setCurrentRange] = useState<ICurrentRange>({
        start: moment().startOf('month').format(currentRangeUrlQueryFormat),
        end: moment().endOf('month').add(1, 'day').format(currentRangeUrlQueryFormat), // add one day to include the last day of the month due to timezone weirdness
    });

    const pathQueryParams = useSchoolFeedContentPathQueryParams();

    let initialEvents: EventContentItem[] = [];
    if (props.initialEventsPromise) {
        initialEvents = use(props.initialEventsPromise);
    }

    const {data: events} = useSWR(JSON.stringify(currentRange), async (key) => {
        const parsedKey = JSON.parse(key);
        const e = await getEventsForRange(pathQueryParams, props.urlQuery, parsedKey.start, parsedKey.end);
        return e.map(e => new ReactiveContentItem(e));
    }, {
        revalidateOnFocus: false,
        revalidateOnMount: false,
        dedupingInterval: 5000,
        fallbackData: initialEvents.map(e => new ReactiveContentItem(e)),
    });

    const initialDate = props.urlQuery.currentSelectedDate ? moment(props.urlQuery.currentSelectedDate, currentRangeUrlQueryFormat).format(currentDateFormat) : undefined;
    const {
        currentMonth,
        currentDate,
        nextMonth,
        prevMonth,
        setCurrentDate
    } = useCleverCalendarData(setCurrentRange, initialDate);
    const router = useRouter();

    return <div className={classNames(styles.eventsWidget, styles.widget)}>
        <MonthCalendar
            currentMonth={currentMonth}
            currentDate={currentDate.format(currentDateFormat)}
            setCurrentDate={date => {
                if (props.urlQuery.type === "events" && currentMonth === moment(date, currentDateFormat).format(currentMonthFormat)) {
                    // we're already on the correct date so we don't need to refetch data or hard navigate, just update UI
                    setCurrentDate(moment(date, currentDateFormat));
                    SafeRouter.setQuery("currentSelectedDate", moment(date, currentDateFormat).format(currentRangeUrlQueryFormat));
                } else {
                    const { i18nStore } = useStores(); // If not already in scope
                    const basePath = withLocaleInURI("/feed", i18nStore.currentAppSupportedLocale);

                    const uri = new URI(basePath).search({
                        ...props.urlQuery,
                        type: "events",
                        currentSelectedDate: moment(date, currentDateFormat).format(currentRangeUrlQueryFormat),
                    });

                    router.push(uri.normalize().toString());
                }
            }}
            events={events as IReactiveEventContentItem[]}
            prevMonth={prevMonth}
            nextMonth={nextMonth}
        />
    </div>
}

export function SchoolFeedMonthCalendar(props: {
    initialEventsPromise: Promise<EventContentItem[]>,
    urlQuery: ISchoolFeedUrlQueries,
}) {
    return <Suspense fallback={<SchoolFeedMonthCalendarSkeleton
        urlQuery={props.urlQuery}
    />}>
        <SchoolFeedMonthCalendarSkeleton
            urlQuery={props.urlQuery}
            initialEventsPromise={props.initialEventsPromise}
        />
    </Suspense>
}
